import { useMemo, type ComponentType } from 'react';
import { Box, SimpleGrid, Text, type IconProps } from '@chakra-ui/react';
import Select from 'react-select';
import {
  PhosphorAirplaneTiltIcon,
  PhosphorCheckIcon,
  PhosphorCodeIcon,
  PhosphorCreditCardIcon,
  PhosphorDotsThreeIcon,
  PhosphorIdentificationCardIcon,
  PhosphorIntersectIcon,
  PhosphorKeyIcon,
  PhosphorQuestionIcon,
  PhosphorSquaresFourIcon,
  PhosphorTagIcon,
  PhosphorTicketIcon,
} from '../../../theme/icons/phosphor';
import { passBuilderSelectStyles } from '../../../theme/reactSelectStyles';
import {
  getCountryOptions,
  type SurveyOption,
  type SurveyOptionIcon,
  type SurveyQuestion,
} from './onboardingSurveyQuestions';

type FieldValue = string | string[];

interface OnboardingSurveyFieldProps {
  question: SurveyQuestion;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  labelId?: string;
}

const SELECTED_BG = 'orangePrimaryLight';
const SELECTED_BORDER = 'orangePrimary';
const SELECTED_TEXT = 'orangePrimary';

const GREY_BORDER = 'linear-gradient(#D9D9D9, #D9D9D9)';
const GRADIENT_BORDER = 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)';
const outlined = (fill: string) => ({
  border: '1px solid transparent',
  background: `linear-gradient(${fill}, ${fill}) padding-box, ${GREY_BORDER} border-box`,
  _hover: {
    background: `linear-gradient(${fill}, ${fill}) padding-box, ${GRADIENT_BORDER} border-box`,
  },
});

const surveySelectStyles = {
  ...passBuilderSelectStyles,
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
};

const OPTION_ICONS: Record<SurveyOptionIcon, ComponentType<IconProps>> = {
  dashboard: PhosphorSquaresFourIcon,
  api: PhosphorCodeIcon,
  both: PhosphorIntersectIcon,
  help: PhosphorQuestionIcon,
  ticket: PhosphorTicketIcon,
  card: PhosphorCreditCardIcon,
  id: PhosphorIdentificationCardIcon,
  key: PhosphorKeyIcon,
  plane: PhosphorAirplaneTiltIcon,
  tag: PhosphorTagIcon,
  dots: PhosphorDotsThreeIcon,
};

const CheckIcon = ({ boxSize = '18px' }: { boxSize?: string }) => (
  <PhosphorCheckIcon boxSize={boxSize} aria-hidden />
);

const OptionIcon = ({
  icon,
  boxSize = '24px',
}: {
  icon: SurveyOptionIcon;
  boxSize?: string;
}) => {
  const Glyph = OPTION_ICONS[icon];
  return <Glyph boxSize={boxSize} aria-hidden />;
};

const PillOptions = ({
  options,
  selected,
  onToggle,
  columns,
  isMulti,
  labelId,
}: {
  options: SurveyOption[];
  selected: string[];
  onToggle: (value: string) => void;
  columns: Record<string, number>;
  isMulti: boolean;
  labelId?: string;
}) => {
  const hasIcons = options.some((option) => option.icon);
  return (
    <SimpleGrid
      columns={columns}
      spacing={3}
      role={isMulti ? 'group' : 'radiogroup'}
      aria-labelledby={labelId}
    >
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        return (
          <Box
            key={option.value}
            as="button"
            type="button"
            role={isMulti ? 'checkbox' : 'radio'}
            aria-checked={isSelected}
            onClick={() => onToggle(option.value)}
            display="flex"
            alignItems="center"
            justifyContent={hasIcons ? 'flex-start' : 'center'}
            gap={2.5}
            w="full"
            borderRadius="full"
            px={5}
            py={2.5}
            textStyle="bodySmall"
            fontWeight="600"
            border="1px solid"
            transition="background 0.15s, color 0.15s, border-color 0.15s"
            color={isSelected ? SELECTED_TEXT : 'inkBlue'}
            bg={isSelected ? SELECTED_BG : undefined}
            borderColor={isSelected ? SELECTED_BORDER : undefined}
            sx={isSelected ? undefined : outlined('#fff')}
          >
            {isSelected ? (
              <CheckIcon boxSize="18px" />
            ) : option.icon ? (
              <OptionIcon icon={option.icon} boxSize="18px" />
            ) : null}
            <Box as="span">{option.label}</Box>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

const CardOptions = ({
  options,
  value,
  onSelect,
  labelId,
}: {
  options: SurveyOption[];
  value: string;
  onSelect: (value: string) => void;
  labelId?: string;
}) => (
  <SimpleGrid
    columns={2}
    spacing={3}
    role="radiogroup"
    aria-labelledby={labelId}
  >
    {options.map((option) => {
      const isSelected = value === option.value;
      return (
        <Box
          key={option.value}
          as="button"
          type="button"
          role="radio"
          aria-checked={isSelected}
          onClick={() => onSelect(option.value)}
          textAlign="left"
          borderRadius="12px"
          p={4}
          border="1px solid"
          transition="background 0.15s, color 0.15s, border-color 0.15s"
          color={isSelected ? SELECTED_TEXT : 'inkBlue'}
          bg={isSelected ? SELECTED_BG : undefined}
          borderColor={isSelected ? SELECTED_BORDER : undefined}
          sx={isSelected ? undefined : outlined('#fff')}
        >
          {option.icon && (
            <Box mb={2}>
              <OptionIcon icon={option.icon} />
            </Box>
          )}
          <Text textStyle="bodySmall" fontWeight="600">
            {option.label}
          </Text>
          {option.sublabel && (
            <Text
              fontFamily="body"
              fontSize="13px"
              mt={0.5}
              color={isSelected ? SELECTED_TEXT : 'grey'}
            >
              {option.sublabel}
            </Text>
          )}
        </Box>
      );
    })}
  </SimpleGrid>
);

const SegmentedOptions = ({
  options,
  value,
  onSelect,
  labelId,
}: {
  options: SurveyOption[];
  value: string;
  onSelect: (value: string) => void;
  labelId?: string;
}) => (
  <SimpleGrid
    columns={{ base: 2, sm: options.length }}
    spacing={1}
    borderRadius="16px"
    p={1.5}
    border="1px solid"
    borderColor="lightGrey"
    role="radiogroup"
    aria-labelledby={labelId}
  >
    {options.map((option) => {
      const isSelected = value === option.value;
      return (
        <Box
          key={option.value}
          as="button"
          type="button"
          role="radio"
          aria-checked={isSelected}
          onClick={() => onSelect(option.value)}
          py={2.5}
          px={2}
          textAlign="center"
          whiteSpace="nowrap"
          borderRadius="10px"
          textStyle="bodySmall"
          fontWeight={isSelected ? '700' : '600'}
          bg={isSelected ? SELECTED_BG : 'transparent'}
          color={isSelected ? SELECTED_TEXT : 'inkBlue'}
          transition="background 0.15s, color 0.15s"
          _hover={isSelected ? undefined : { bg: 'softWhite' }}
        >
          {option.label}
        </Box>
      );
    })}
  </SimpleGrid>
);

const SurveySelect = ({
  question,
  value,
  onChange,
  labelId,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (value: string) => void;
  labelId?: string;
}) => {
  const options = useMemo<SurveyOption[]>(() => {
    if (question.dynamicOptions === 'country') return getCountryOptions();
    return question.options ?? [];
  }, [question]);
  const selectedOption = options.find((o) => o.value === value) ?? null;

  return (
    <Select
      inputId={question.id}
      aria-labelledby={labelId}
      options={options}
      value={selectedOption}
      onChange={(option) =>
        onChange((option as SurveyOption | null)?.value ?? '')
      }
      placeholder="Select..."
      styles={surveySelectStyles}
      isSearchable
      menuPlacement="auto"
      menuPortalTarget={
        typeof document !== 'undefined' ? document.body : undefined
      }
    />
  );
};

const OnboardingSurveyField = ({
  question,
  value,
  onChange,
  labelId,
}: OnboardingSurveyFieldProps) => {
  const options = question.options ?? [];

  if (question.type === 'pills' || question.type === 'pills-multi') {
    const isMulti = question.type === 'pills-multi';
    const selected = isMulti
      ? Array.isArray(value)
        ? value
        : []
      : typeof value === 'string' && value
        ? [value]
        : [];
    const onToggle = (val: string) => {
      if (!isMulti) {
        onChange(val);
        return;
      }
      onChange(
        selected.includes(val)
          ? selected.filter((v) => v !== val)
          : [...selected, val]
      );
    };
    return (
      <PillOptions
        options={options}
        selected={selected}
        onToggle={onToggle}
        columns={isMulti ? { base: 1, sm: 2 } : { base: 2, md: 3 }}
        isMulti={isMulti}
        labelId={labelId}
      />
    );
  }

  if (question.type === 'cards') {
    return (
      <CardOptions
        options={options}
        value={typeof value === 'string' ? value : ''}
        onSelect={onChange}
        labelId={labelId}
      />
    );
  }

  if (question.type === 'segmented') {
    return (
      <SegmentedOptions
        options={options}
        value={typeof value === 'string' ? value : ''}
        onSelect={onChange}
        labelId={labelId}
      />
    );
  }

  return (
    <SurveySelect
      question={question}
      value={typeof value === 'string' ? value : ''}
      onChange={onChange}
      labelId={labelId}
    />
  );
};

export default OnboardingSurveyField;
